import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"
import { useEffect, useCallback } from "react"
import { RootState, AppDispatch } from "../state/store"
import {
  setActiveSection,
  mountSection,
  setSectionSubRoute,
  forceSectionReload,
  SectionName,
} from "../state/sectionSlice"
import {
  parseRouteFromPath,
  constructSectionPath,
  normalizeSubRoute,
  extractSubRoute,
  getSectionHome,
} from "../utils/routeSync"

export const useSection = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { activeSection, lastActiveSection, mountedSections, sectionSubRoutes, sectionReloadTriggers } = useSelector(
    (state: RootState) => state.section,
  )

  // Helper to navigate while preserving the preview search param.
  // Uses react-router's { pathname, search } option to set params cleanly (no duplication).
  const navigateWithPreview = useCallback(
    (path: string, options?: { replace?: boolean }) => {
      const preview = searchParams.get("preview")
      const [pathname, searchStr] = path.split("?")
      const params = new URLSearchParams(searchStr || "")
      if (preview) {
        params.set("preview", preview)
      }
      const search = params.toString() ? `?${params.toString()}` : ""
      navigate({ pathname, search }, options)
    },
    [searchParams, navigate],
  )

  // Sync active section with current route using route utilities
  useEffect(() => {
    const routeInfo = parseRouteFromPath(location.pathname)

    if (routeInfo) {
      dispatch(
        setActiveSection({
          section: routeInfo.sectionName,
          subRoute: routeInfo.subRoute,
        }),
      )
    } else {
      // Not a section route (e.g., home page, auth, etc.)
      dispatch(setActiveSection({ section: null }))
    }
  }, [location.pathname, dispatch])

  const navigateToSection = (sectionName: SectionName, subRoute?: string, forceHome?: boolean) => {
    // If forceHome is true, always go to section home
    if (forceHome) {
      batchUpdateSectionRoute(sectionName, "/").catch((error) => {
        console.error(`Failed to navigate to section home ${sectionName}:`, error)
      })
      return
    }

    // If no sub-route is provided, use the stored sub-route for this section (if any)
    const targetSubRoute = subRoute || getSectionSubRoute(sectionName)
    batchUpdateSectionRoute(sectionName, targetSubRoute).catch((error) => {
      console.error(`Failed to navigate to section ${sectionName}:`, error)
    })
  }

  const navigateToSectionHome = (sectionName: SectionName) => {
    const path = getSectionHome(sectionName)

    // Clear the stored sub-route when explicitly navigating to home
    dispatch(setSectionSubRoute({ section: sectionName, subRoute: "/" }))

    // Navigate to the URL first (preserving preview param)
    navigateWithPreview(path)

    // Force section to reload with the new route - this ensures the section's
    // MemoryRouter gets re-initialised with the correct home route
    dispatch(forceSectionReload(sectionName))
  }

  const resetSectionSubRoute = (sectionName: SectionName) => {
    dispatch(setSectionSubRoute({ section: sectionName, subRoute: "/" }))
  }

  const updateSectionSubRoute = (sectionName: SectionName, subRoute: string) => {
    const normalizedSubRoute = normalizeSubRoute(subRoute)
    dispatch(setSectionSubRoute({ section: sectionName, subRoute: normalizedSubRoute }))
  }

  const getSectionSubRoute = (sectionName: SectionName): string => {
    return sectionSubRoutes[sectionName] || "/"
  }

  // Atomic batch update for section route changes
  const batchUpdateSectionRoute = (sectionName: SectionName, subRoute: string) => {
    return new Promise<void>((resolve, reject) => {
      try {
        const normalizedSubRoute = normalizeSubRoute(subRoute)
        const fullPath = constructSectionPath(sectionName, normalizedSubRoute)

        // Batch Redux updates to ensure atomicity
        dispatch((dispatch: AppDispatch, getState: () => RootState) => {
          // Update section sub-route

          dispatch(setSectionSubRoute({ section: sectionName, subRoute: normalizedSubRoute }))

          // Ensure section is marked as active if not already
          const state = getState()
          const needsActiveSectionUpdate = state.section.activeSection !== sectionName

          if (needsActiveSectionUpdate) {
            dispatch(
              setActiveSection({
                section: sectionName,
                subRoute: normalizedSubRoute,
              }),
            )
          }
        })

        // Navigate after state update (preserving preview param)

        navigateWithPreview(fullPath)

        resolve()
      } catch (error) {
        console.error(`Failed to batch update section route for ${sectionName}:`, error)
        reject(error)
      }
    })
  }

  // New method to update section route and sync with browser URL (now using atomic updates)
  const updateSectionRoute = (sectionName: SectionName, subRoute: string) => {
    batchUpdateSectionRoute(sectionName, subRoute).catch((error) => {
      console.error(`Failed to update section route for ${sectionName}:`, error)
    })
  }

  // Extract sub-route from current path for a specific section
  const extractCurrentSubRoute = (sectionName: SectionName): string => {
    return extractSubRoute(location.pathname, sectionName)
  }

  // Verify and recover section route synchronization
  const verifySectionSync = (sectionName: SectionName) => {
    const currentSubRoute = extractSubRoute(location.pathname, sectionName)
    const storedSubRoute = getSectionSubRoute(sectionName)

    if (currentSubRoute !== storedSubRoute) {
      // Force sync by updating stored state to match browser URL
      updateSectionSubRoute(sectionName, currentSubRoute)

      return false // Indicates sync was needed
    }

    return true // Already in sync
  }

  const navigateToHome = () => {
    navigateWithPreview("/")
  }

  const isSectionMounted = (sectionName: SectionName): boolean => {
    return mountedSections.includes(sectionName)
  }

  const mountSectionIfNeeded = (sectionName: SectionName) => {
    if (!mountedSections.includes(sectionName)) {
      dispatch(mountSection(sectionName))
    }
  }

  return {
    activeSection,
    lastActiveSection,
    mountedSections,
    sectionSubRoutes,
    sectionReloadTriggers,
    navigateToSection,
    navigateToSectionHome,
    resetSectionSubRoute,
    navigateToHome,
    isSectionMounted,
    mountSectionIfNeeded,
    updateSectionSubRoute,
    getSectionSubRoute,
    updateSectionRoute,
    extractCurrentSubRoute,
    verifySectionSync,
    batchUpdateSectionRoute,
  }
}
